import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { authdApiRequest } from '../utils/api';
import Layout from '../components/Layout';
import DetailModal from '../components/DetailModal';
import { MessageSquare } from 'react-feather';
import Tabs from '../components/ResultTabs';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { Info } from 'react-feather';
import { Tooltip } from 'react-tooltip'
import { jwtDecode } from 'jwt-decode';

function SnapshotResults() {
  const { snapshotId } = useParams();
  const [results, setResults] = useState([]);
  const [teams, setTeams] = useState([]);
  const [comments, setComments] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState('');
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'rank', direction: 'ascending' });
  const [snapshot, setSnapshotDetails] = useState(null);
  const [allocations, setAllocations] = useState({ overallAverages: {}, teamAverages: [] });
  const [overallScores, setOverallScores] = useState({ overallScore: {}, teamOverallScores: [] });
  const [viewMode, setViewMode] = useState('score'); // 'score' or 'focus'
  const [triageStatus, setTriageStatus] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);

  const isUserTeamLead = React.useMemo(() => {
    const userDataStr = localStorage.getItem('user');
    if (!userDataStr) return false;
    
    try {
      const userData = JSON.parse(userDataStr);
      return userData.organizations.some(org => 
        org.teams.some(team => team.role === 'lead')
      );
    } catch (error) {
      console.error('Error parsing user data:', error);
      return false;
    }
  }, []);

  const sortedResults = React.useMemo(() => {
    let sortableResults = [...results];

    if (selectedTeam) {
      // Filter and map results based on the selected team
      sortableResults = sortableResults.map(topic => {
        const teamData = topic.teams.find(team => team.teamId.toString() === selectedTeam);
        if (teamData) {
          return {
            ...topic,
            rank: teamData.rank,
            score: teamData.score,
            focusCount: teamData.focusCount,
            focusRank: teamData.focusRank,
            teamData: teamData
          };
        }
        return topic;
      });
    }

    if (sortConfig !== null) {
      sortableResults.sort((a, b) => {
        const aValue = selectedTeam ? a.teamData[sortConfig.key] : a[sortConfig.key];
        const bValue = selectedTeam ? b.teamData[sortConfig.key] : b[sortConfig.key];

        if (aValue === null || aValue === 'NR') return 1;
        if (bValue === null || bValue === 'NR') return -1;

        if (aValue < bValue) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableResults;
  }, [results, sortConfig, selectedTeam]);

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  useEffect(() => {
    const fetchSnapshotResults = async () => {
      try {
        const response = await authdApiRequest(
          `${process.env.REACT_APP_API_HOST}/surveys/${snapshotId}/results`
        );
        const data = await response.json();

        // Set the snapshot details, allocations, and results from the response
        setSnapshotDetails(data.snapshot);
        setAllocations(data.allocations);
        setResults(data.topics);
        setOverallScores(data.overallScores);

        // Combine all comments from freeTextJson arrays into a single array with topic names
        const allComments = data.topics.flatMap(topic => {
          // If no team is selected, include all comments
          if (!selectedTeam) {
            return topic.freeTextJson.map(comment => ({
              ...comment,
              topicName: topic.topicName // Attach the topic name to each comment
            }));
          }

          // Filter comments based on the selected team
          const filteredComments = topic.freeTextJson.filter(comment =>
            comment.user.teams.some(team => team.id.toString() === selectedTeam)
          );

          return filteredComments.map(comment => ({
            ...comment,
            topicName: topic.topicName // Attach the topic name to each comment
          }));
        });

        // Shuffle the combined comments array
        const shuffledComments = shuffleArray(allComments);

        // Set the comments state
        setComments(shuffledComments);
      } catch (error) {
        console.error('Error fetching snapshot results:', error);
        toast.error('Failed to load snapshot results.');
      }
    };

    const fetchTeams = async () => {
      try {
        const response = await authdApiRequest(`${process.env.REACT_APP_API_HOST}/teams`);
        const data = await response.json();
        setTeams(data);
      } catch (error) {
        toast.error('Error fetching teams.');
        console.error('Error fetching teams:', error);
      }
    };

    const fetchTriageStatus = async () => {
      try {
        const response = await authdApiRequest(
          `${process.env.REACT_APP_API_HOST}/surveys/${snapshotId}/triage-status`
        );
        const data = await response.json();
        setTriageStatus(data);
      } catch (error) {
        console.error('Error fetching triage status:', error);
      }
    };

    fetchSnapshotResults();
    fetchTeams();
    fetchTriageStatus();
  }, [navigate, snapshotId, selectedTeam]);

  useEffect(() => {
    // Assuming you have a function to get the JWT token
    const token = localStorage.getItem('jwt'); // Or wherever you store the token

    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        console.log(decodedToken.isAdmin || false);
        setIsAdmin(decodedToken.isAdmin || false); // Adjust based on your token structure
      } catch (error) {
        console.error('Error decoding token:', error);
      }
    }
  }, []);

  const handleTeamChange = (e) => {
    const teamId = e.target.value;
    setSelectedTeam(teamId);

    // Filter results to get data for the selected team
    const filteredResults = results.map(topic => {
      const teamData = topic.teams.find(team => team.teamId.toString() === teamId);
      return {
        ...topic,
        teamData: teamData || {} // Attach the team data or an empty object if not found
      };
    });

    const filteredComments = results.flatMap(topic => {
      if (!teamId) {
        // If no team is selected, include all comments
        return topic.freeTextJson.map(comment => ({
          ...comment,
          topicName: topic.topicName // Attach the topic name to each comment
        }));
      }
  
      // Filter comments based on the selected team
      return topic.freeTextJson.filter(comment =>
        comment.user.teams.some(team => team.id.toString() === teamId)
      ).map(comment => ({
        ...comment,
        topicName: topic.topicName // Attach the topic name to each comment
      }));
    });
  
    // Shuffle the combined comments array
    const shuffledComments = shuffleArray(filteredComments);
  
    setComments(shuffledComments);
    setResults(filteredResults);
  };

  const handleTopicClick = async (topicId) => {
    try {
      const response = await authdApiRequest(
        `${process.env.REACT_APP_API_HOST}/surveys/${snapshotId}/results/${topicId}/details`
      );
      const data = await response.json();
      setSelectedTopic(data);
      setIsModalOpen(true);
    } catch (error) {
      console.error('Error fetching topic details:', error);
      toast.error('Failed to load topic details.');
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedTopic(null);
  };

  const getBarColor = (score) => {
    if (score < 0.5) {
      return `rgb(${255}, ${Math.floor(255 * score * 2)}, 0)`;
    } else {
      return `rgb(${Math.floor(255 * (1 - score) * 2)}, 255, 0)`;
    }
  };

  const maxFocusCount = Math.max(...results.map(topic => topic.focusCount));

  // Utility function to shuffle an array
  const shuffleArray = (array) => {
    return array.sort(() => Math.random() - 0.5);
  };

  // Determine which data to use for the pie chart
  const currentAverages = selectedTeam
    ? allocations.teamAverages.find(team => team.teamId.toString() === selectedTeam)?.averages
    : allocations.overallAverages;

  // Prepare data for the pie chart
  const pieData = currentAverages ? [
    { name: 'New Features', value: currentAverages.newPct },
    { name: 'Old Features', value: currentAverages.oldPct },
    { name: 'Help', value: currentAverages.helpPct },
    { name: 'Other', value: currentAverages.otherPct },
  ] : [];

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

  // Prepare data for the heatmap table
  const heatmapData = React.useMemo(() => {
    if (!results) return [];

    return results.map(topic => {
      const overallScore = topic.score || 0;
      const focusCount = topic.focusCount || 0;
      const teamScores = topic.teams.map(team => {
        return {
          teamName: team.teamName,
          teamScore: team.score || 0,
          teamFocus: team.focusCount || 0,
          scoreDifference: (team.score || 0) - overallScore,
        };
      });
      return {
        topicName: topic.topicName,
        overallScore,
        focusCount,
        teamScores
      };
    });
  }, [results]);

  // Function to determine the inline style based on score difference
  const getScoreDifferenceStyle = (difference) => {
    const absDifference = Math.abs(difference);
    const maxIntensity = 220; // Slightly increased maximum intensity for brighter colors
    const minIntensity = 120; // Slightly increased minimum intensity for brighter colors

    if (difference > 0) {
      // Brighter shades of forest green for positive differences
      const greenIntensity = Math.max(minIntensity, maxIntensity - Math.floor(maxIntensity * absDifference));
      return { backgroundColor: `rgba(34, ${greenIntensity}, 34, 0.3)` }; // Forest green
    } else if (difference < 0) {
      // Brighter shades of yellow/orange for negative differences
      const yellowIntensity = Math.max(minIntensity, maxIntensity - Math.floor(maxIntensity * absDifference));
      return { backgroundColor: `rgba(${yellowIntensity}, ${yellowIntensity}, 0, 0.3)` }; // Yellow/Orange
    }
    return { backgroundColor: 'rgba(128, 128, 128, 0.3)' }; // Neutral grey for no difference
  };

  // Function to determine the inline style based on focus count
  const getFocusCountStyle = (focusCount, maxFocusCount) => {
    const intensity = Math.floor((focusCount / maxFocusCount) * 255);
    return { backgroundColor: `rgba(0, 0, ${intensity}, 0.5)` }; // Blue gradient
  };

  const selectedTeamOverallScore = React.useMemo(() => {
    if (!selectedTeam) return overallScores.overallScore;
    const teamScore = overallScores.teamOverallScores.find(
      team => team.teamId.toString() === selectedTeam
    );
    return teamScore ? teamScore.overallScore : null;
  }, [overallScores, selectedTeam]);

  return (
    <Layout>
      <div>
        <h1 className="text-2xl font-bold mb-8 text-center">
          {snapshot ? snapshot.title ? (
            `${formatDate(snapshot.endDate)} Snapshot (${snapshot.title})`
          ) : (
            `${formatDate(snapshot.endDate)} Snapshot`
          ) : "Snapshot"}
        </h1>

        {(isAdmin || isUserTeamLead) && (
          <div className="mb-4 p-4 bg-neutral-900 rounded-lg text-center text-sm">
            <h2 className="text-lg font-semibold mb-2">Triage Status</h2>
            <ul className="list-none mb-4">
              {triageStatus
                .filter(status => 
                  isAdmin || 
                  (isUserTeamLead && (() => {
                    const userData = JSON.parse(localStorage.getItem('user'));
                    return userData.organizations.some(org => 
                      org.teams.some(team => 
                        team.name === status.teamName && team.role === 'lead'
                      )
                    );
                  })())
                )
                .map((status, index) => (
                  <li key={index} className="flex items-center py-2 px-4 bg-neutral-800 rounded-md mb-2">
                    <span className="font-medium w-1/3 text-left">{status.teamName}</span>
                    <span className={`font-semibold w-1/3 text-center ${status.triaged ? 'text-green-500' : 'text-red-500'}`}>
                      {status.triaged ? 'Completed' : 'Pending'}
                    </span>
                    <div className="w-1/3 flex justify-end">
                      {(isAdmin || (() => {
                        const userData = JSON.parse(localStorage.getItem('user'));
                        return userData.organizations && userData.organizations.some(org => 
                          org.teams && org.teams.some(team => 
                            team.name === status.teamName && team.role === 'lead'
                          )
                        );
                      })()) && (
                        <button
                          onClick={() => navigate(`/snapshots/${snapshotId}/triage/${status.teamId}`)}
                          className={`px-2 py-1 rounded-md text-white text-xs ${
                            status.triaged ? 'bg-green-500' : 'bg-amber-500'
                          }`}
                        >
                          {status.triaged ? 'Update Triage' : 'Triage Required'}
                        </button>
                      )}
                    </div>
                  </li>
                ))}
            </ul>
            {(isAdmin || isUserTeamLead) && (
              <button
                onClick={() => navigate(`/snapshots/${snapshotId}/triage/status`)}
                className="text-blue-500 underline hover:text-blue-400"
              >
                View Triage Results
              </button>
            )}
          </div>
        )}
        
        <div className="mb-4 text-center text-sm">
          <div>
            <label htmlFor="team-select" className="mr-2">Team:</label>
            <select id="team-select" value={selectedTeam} onChange={handleTeamChange} className="p-2 border text-sm">
              <option value="">All Teams</option>
              {teams.map((team) => (
                <option key={team.id} value={team.id}>{team.name}</option>
              ))}
            </select>
          </div>
        </div>

      </div>
      <Tabs>
        <div title="Sentiment">
        {selectedTeamOverallScore !== null && (
          <div className="flex items-center justify-center items-center bg-black border border-neutral-800 rounded p-4 mb-4 text-center">
            <h2 className="text-xl font-semibold">
              Overall Experience Index:   
            </h2>
            <div className="flex items-center justify-center ml-2">
              <span className="text-2xl font-bold mr-2">
                {(selectedTeamOverallScore * 100).toFixed()}%
              </span>
              <div className="w-24 h-4 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full"
                  style={{
                    width: `${selectedTeamOverallScore * 100}%`,
                    backgroundColor: getBarColor(selectedTeamOverallScore),
                  }}
                ></div>
              </div>
            </div>
            <Info
             size={16}
             className="ml-2"
             data-tooltip-id="oei-tip"
             data-tooltip-content="The Overall Experience Index represents the average of all scores."
            />
            <Tooltip id="oei-tip" />
          </div>
        )}

          <table className="w-full bg-neutral-900 rounded-lg border border-neutral-700 text-sm">
            <thead>
              <tr>
                <th className="text-left py-2 px-4 border-b-2 border-neutral-700 w-20"></th>
                <th className="text-left py-2 px-4 border-b-2 border-neutral-700 w-6 text-center cursor-pointer" onClick={() => requestSort('rank')}>
                  <div className="flex items-center justify-center">
                    Rank
                    <Info
                      size={14}
                      className="ml-2"
                      data-tooltip-id="priority-tip"
                      data-tooltip-content="Rank of positive view on topic, higher is better"
                    />
                    <Tooltip id="priority-tip" />
                  </div>
                </th>
                <th className="text-left py-2 px-4 border-b-2 border-neutral-700 w-auto cursor-pointer" onClick={() => requestSort('topicName')}>Name</th>
                <th className="text-center py-2 px-4 border-b-2 border-neutral-700 w-40 text-center cursor-pointer" onClick={() => requestSort('focusRank')}>
                  <div className="flex items-center justify-center">
                    Priority
                    <Info
                      size={14}
                      className="ml-2"
                      data-tooltip-id="priority-tip"
                      data-tooltip-content="Number of votes for a topic respondents want to see improved"
                    />
                    <Tooltip id="priority-tip" />
                  </div>
                </th>
                <th className="text-center py-2 px-4 border-b-2 border-neutral-700 w-40 text-center cursor-pointer" onClick={() => requestSort('score')}>
                  <div className="flex items-center justify-center">
                    Score
                    <Info
                      size={14}
                      className="ml-2"
                      data-tooltip-id="score-tip"
                      data-tooltip-content="Percentage of respondents that voted positively for the topic"
                    />
                    <Tooltip id="score-tip" />
                  </div>
                </th>
                <th className="text-left py-2 px-4 border-b-2 border-neutral-700 w-32 text-center">Comments</th>
                <th className="text-left py-2 px-4 border-b-2 border-neutral-700 w-4 text-center"></th>
              </tr>
            </thead>
            <tbody>
              {sortedResults.map((topic) => (
                <tr key={topic.topicId} className="hover:bg-neutral-800" onClick={() => handleTopicClick(topic.topicId)} style={{ cursor: 'pointer' }}>
                  <td className="border-b border-neutral-700 py-2 px-4 w-20">
                    {topic.rankChange === null || topic.rankChange === 0 ? (
                      '-'
                    ) : topic.rankChange > 0 ? (
                      <span className="text-green-500">↑ {topic.rankChange}</span>
                    ) : (
                      <span className="text-red-500">↓ {Math.abs(topic.rankChange)}</span>
                    )}
                  </td>
                  <td className="border-b border-neutral-700 py-2 px-4 w-6 text-center">{selectedTeam ? topic.teamData.rank : topic.rank}</td>
                  <td className="border-b border-neutral-700 py-2 px-4 w-auto font-bold">
                    {topic.topicName}
                  </td>
                  <td className="border-b border-neutral-700 py-2 px-4 w-40 text-center">
                    <div className="flex items-center justify-center">
                      <div className={`mr-2 text-sm w-12 ${topic.focusRank === null ? 'text-neutral-500' : ''}`}>
                        {topic.focusRank === null ? 'NR' : `#${topic.focusRank}`}
                      </div>
                      <div className="relative w-16 h-4 bg-gray-300 rounded">
                        <div
                          className="absolute top-0 left-0 h-4 rounded bg-sky-500 flex items-center justify-center"
                          style={{
                            width: `${(topic.focusCount / maxFocusCount * 100).toFixed(2)}%`,
                          }}
                        ><span className="text-white font-semibold">{topic.focusCount > 0 ? topic.focusCount : ''}</span></div>
                      </div>
                    </div>
                  </td>
                  <td className="border-b border-neutral-700 py-2 px-4 w-40 text-center">
                    <div className="flex items-center justify-center">
                      <div className="relative w-16 h-4 bg-gray-300 rounded">
                        <div
                          className="absolute top-0 left-0 h-4 rounded"
                          style={{
                            width: `${(topic.score * 100).toFixed(2)}%`,
                            backgroundColor: getBarColor(topic.score),
                          }}
                        ></div>
                      </div>
                      <div className="ml-2 text-sm w-8">{(topic.score * 100).toFixed()}%</div>
                    </div>
                  </td>
                  <td className="border-b border-neutral-700 py-2 px-4 w-32 text-center">
                    <div className="flex items-center justify-center">
                      <MessageSquare color="white" size={14} />
                      <span className="pl-2">
                        {Array.isArray(topic.freeTextJson) ? topic.freeTextJson.length : 0}
                      </span>
                    </div>
                  </td>
                  <td className="border-b border-neutral-700 py-2 px-4 text-lg text-center">
                    &gt;
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <DetailModal isOpen={isModalOpen} onClose={closeModal} topic={selectedTopic} />
        </div>
        <div title="Heatmap">
          <div className="mb-4 text-center">
            <button
              onClick={() => setViewMode('score')}
              className={`px-4 py-2 mx-2 ${viewMode === 'score' ? 'bg-sky-500' : 'bg-sky-700'} text-sm text-white rounded`}
            >
              Sentiment
            </button>
            <button
              onClick={() => setViewMode('focus')}
              className={`px-4 py-2 mx-2 ${viewMode === 'focus' ? 'bg-sky-500' : 'bg-sky-700'} text-sm text-white rounded`}
            >
              Priority
            </button>
          </div>
          <table className="w-full bg-neutral-900 rounded-lg border border-neutral-700 text-sm">
            <thead>
              <tr>
                <th className="text-left py-2 px-4 border-b-2 border-neutral-700">Topic</th>
                <th className="py-2 px-4 border-b-2 border-neutral-700 text-center">
                  {viewMode === 'focus' ? 'Priority Votes' : 'Sentiment Score'}
                </th>
                {teams.map((team) => (
                  <th key={team.id} className="text-center py-2 px-4 border-b-2 border-neutral-700">
                    {team.name} 
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {heatmapData.map((topic, index) => (
                <tr key={index} className="hover:bg-neutral-800">
                  <td className="border-b border-neutral-700 py-2 px-4">{topic.topicName}</td>
                  <td className="border-b border-neutral-700 py-2 px-4 text-center">
                    {viewMode === 'focus'
                      ? topic.focusCount
                      : (topic.overallScore * 100).toFixed() + '%'}
                  </td>
                  {topic.teamScores.map((team, teamIndex) => (
                    <td
                      key={teamIndex}
                      className="border-b border-neutral-700 py-2 px-4 w-40 text-center"
                      style={
                        viewMode === 'focus'
                          ? getFocusCountStyle(team.teamFocus, maxFocusCount)
                          : getScoreDifferenceStyle(team.scoreDifference)
                      }
                    >
                      {viewMode === 'focus'
                        ? team.teamFocus
                        : (team.teamScore * 100).toFixed() + `% (${(team.scoreDifference * 100).toFixed()}%)`}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div title="Allocation">
          <ResponsiveContainer width="100%" aspect={3}>
            <PieChart>
              <Pie
                data={pieData}
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                fill="#8884d8"
                dataKey="value"
                isAnimationActive={false}
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div title="Comments">
          {comments.length > 0 ? (
            <ul className="list-none">
              {comments.map((comment, index) => (
                <li key={index} className="p-4 mb-4 bg-neutral-900 border border-neutral-800 rounded-md relative">
                  <div className="mb-4">
                    <span className="text-sm border border-neutral-700 text-neutral-500 rounded-md py-1 px-2">
                      {comment.topicName}
                    </span>
                  </div>
                  <blockquote className="mb-2">
                    "{comment.freeText}"
                  </blockquote>
                  <p className="pt-2 font-semibold">{comment.user.name}</p>
                  {comment.user.teams && comment.user.teams.length > 0 && (
                    <p className="text-sm text-neutral-400">
                      {comment.user.teams.map(team => team.name).join(', ')}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-center pt-4">No comments available</p>
          )}
        </div>
      </Tabs>
    </Layout>
  );
}

export default SnapshotResults;
